import { useMemo } from 'react'

import {
  AnalyticsLevel,
  ResponseCommentDensity,
  ResponseLateDeliverables,
  ResponseNoOfProjects,
} from '@/schema'

import { ViewLevel } from '../constants'
import { useDashboardStore } from '../store'

import {
  useGetCommentDensity,
  useGetLateDeliverables,
  useGetNoOfProjects,
} from './index'

type TUseOrgLevelMetricResult = {
  projectMetric: ResponseNoOfProjects
  lateDeliverablesMetric: ResponseLateDeliverables
  commentDensityMetric: ResponseCommentDensity
  isLoadingNoOfProjects: boolean
  isLoadingLateDeliverables: boolean
  isLoadingCommentDensity: boolean
}

export const useOrgLevelMetric = (): TUseOrgLevelMetricResult => {
  const { filters, viewLevel } = useDashboardStore()

  const {
    data: { data: [projectMetric] = [] } = {},
    isLoading: isLoadingNoOfProjects,
  } = useGetNoOfProjects(
    { company_id: filters.companyId },
    { query: { staleTime: 1000, enabled: viewLevel === ViewLevel.ORG } }
  )

  const {
    data: { data: [lateDeliverablesMetric] = [] } = {},
    isLoading: isLoadingLateDeliverables,
  } = useGetLateDeliverables(
    {
      level: filters.projectId
        ? AnalyticsLevel.project
        : AnalyticsLevel.company,
      company_id: filters.companyId,
      project_id: filters.projectId,
    },
    {
      query: {
        staleTime: 1000,
        enabled: viewLevel === ViewLevel.ORG,
      },
    }
  )

  const analyticLevel = useMemo(() => {
    // Deliverable set level
    if (filters.deliverableSetId) return AnalyticsLevel.deliverable_set
    // Project level
    if (filters.projectId) return AnalyticsLevel.project
    // Default to Company level
    return AnalyticsLevel.company
  }, [filters.deliverableSetId, filters.projectId])

  const {
    data: { data: [commentDensityMetric] = [] } = {},
    isLoading: isLoadingCommentDensity,
  } = useGetCommentDensity(
    {
      level: analyticLevel,
      company_id: filters.companyId,
      project_id: filters.projectId,
      deliverable_set_id: filters.deliverableSetId,
    },
    {
      query: {
        staleTime: 1000,
        enabled: viewLevel === ViewLevel.ORG,
      },
    }
  )

  return useMemo(
    () => ({
      projectMetric,
      isLoadingNoOfProjects,
      lateDeliverablesMetric,
      isLoadingLateDeliverables,
      commentDensityMetric,
      isLoadingCommentDensity,
    }),
    [
      commentDensityMetric,
      lateDeliverablesMetric,
      projectMetric,
      isLoadingNoOfProjects,
      isLoadingLateDeliverables,
      isLoadingCommentDensity,
    ]
  )
}
